import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import { CheckCircle, Cancel, ArrowBack, Payment, Person, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getStripe } from '../utils/stripe';

// 1. Componenta pentru formularul de card
function HostSubscriptionCheckout({ token, plan, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardholderName, setCardholderName] = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return setError('Sistemul de plata nu este disponibil');
    if (!cardholderName.trim()) return setError('Numele titularului cardului este obligatoriu');

    setProcessing(true);
    setError('');

    try {
      const { data } = await axios.post(
        '/api/payment/create-host-subscription',
        { subscriptionType: plan.id, cardholderName: cardholderName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: cardholderName.trim() }
        }
      });

      if (result.error) setError(result.error.message);
      else if (result.paymentIntent?.status === 'succeeded') onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Eroare la procesarea platii');
    }
    setProcessing(false);
  };

  const cardElementOptions = {
    style: {
      base: { fontSize: '16px', color: '#424770', fontFamily: '"Roboto", sans-serif', '::placeholder': { color: '#aab7c4' } },
      invalid: { color: '#9e2146' },
    },
    hidePostalCode: true,
  };

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Nume titular card"
        value={cardholderName}
        onChange={(e) => setCardholderName(e.target.value)}
        required
        margin="dense"
        variant="outlined"
        InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }}
      />
      <Typography variant="subtitle2" sx={{ color: '#006d77', fontWeight: 'bold', mt: 1 }}> Detalii card </Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, borderColor: '#ccc' }}>
        <CardElement options={cardElementOptions} />
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem' }}>{error}</Alert>}
      <Button
        fullWidth
        variant="contained"
        onClick={handlePay}
        disabled={!stripe || processing || !cardholderName.trim()}
        startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <Payment />}
        sx={{ py: 1.2, backgroundColor: '#dd4124', fontFamily: 'Orbitron, sans-serif', fontWeight: 'bold', fontSize: '0.85rem', '&:hover': { backgroundColor: '#b0331c' }, '&:disabled': { backgroundColor: '#ccc' } }}
      >
        {processing ? 'SE PROCESEAZA...' : `PLATESTE ${plan.totalPrice} RON`}
      </Button>
      <Box sx={{ mt: 1.5, p: 1, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: '#666' }}>
          <Lock sx={{ fontSize: '0.9rem', mr: 0.5, color: '#4caf50' }} />
          Platile sunt procesate in siguranta prin Stripe.
        </Typography>
      </Box>
    </Box>
  );
}

// 2. Pagina principala
export default function PaymentPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stripeObject, setStripeObject] = useState(null);

  const plans = [
    { id: 'plan1', monthsPaid: 1, bonusMonths: 0, pricePerMonth: 19, name: 'BASIC' },
    { id: 'plan2', monthsPaid: 5, bonusMonths: 1, pricePerMonth: 19, name: 'PRO' },
    { id: 'plan3', monthsPaid: 9, bonusMonths: 3, pricePerMonth: 19, name: 'PREMIUM' }
  ].map(p => ({
    ...p,
    totalMonths: p.monthsPaid + p.bonusMonths,
    totalPrice: p.monthsPaid * p.pricePerMonth,
    monthlyPrice: (p.monthsPaid * p.pricePerMonth) / (p.monthsPaid + p.bonusMonths)
  }));

  // INITIALIZARE STRIPE - Controlată prin async/await
  useEffect(() => {
    const initStripe = async () => {
      try {
        const instance = await getStripe();
        if (instance) {
          setStripeObject(instance);
        } else {
          setError("Sistemul de plăți nu a putut fi inițializat.");
        }
      } catch (e) {
        console.error("Stripe initialization failed:", e);
        setError("Eroare la conectarea cu Stripe.");
      }
    };
    initStripe();
  }, []);

  // INCARCARE DATE ABONAMENT
  useEffect(() => {
    if (user?.role !== 'host' || !token) {
      setLoading(false);
      return;
    }
    const fetchSub = async () => {
      try {
        const res = await axios.get('/api/host/subscription', { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setSubscription(res.data);
      } catch (err) {
        setError('Eroare la incarcarea datelor despre abonament.');
      } finally {
        setLoading(false);
      }
    };
    fetchSub();
  }, [token, user]);

  if (user?.role !== 'host') return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Alert severity="error">Acces rezervat gazdelor.</Alert>
    </Container>
  );

  // Garanție: Nu randerăm interfața până nu avem și datele și obiectul Stripe
  if (loading || !stripeObject) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#006d77' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fcfcfc' }}>
      <Paper elevation={0} sx={{ backgroundColor: '#006d77', py: 1.5, px: 2, borderRadius: 0 }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}><ArrowBack /></IconButton>
            <Typography variant="h6" sx={{ fontFamily: 'Orbitron, sans-serif', fontWeight: 'bold', color: 'white', flex: 1 }}>
              ABONAMENT HOST
            </Typography>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="sm" sx={{ py: 2, px: 1.5 }}>
        {/* Status Box */}
        <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', backgroundColor: 'white', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {subscription?.subscriptionPaid ? <CheckCircle sx={{ color: '#4caf50', fontSize: 24 }} /> : <Cancel sx={{ color: '#dd4124', fontSize: 24 }} />}
            <Box>
              <Typography variant="body2" sx={{ fontFamily: 'Orbitron, sans-serif', color: '#666', fontSize: '0.7rem' }}>STATUS CURENT</Typography>
              <Typography variant="h6" sx={{ color: subscription?.subscriptionPaid ? '#4caf50' : '#dd4124', fontWeight: 'bold', lineHeight: 1 }}>
                {subscription?.subscriptionPaid ? 'ACTIV' : 'INACTIV'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {subscription?.subscriptionPaid ? (
          <Alert severity="success">Abonamentul tau este activ. Poti adauga proprietati noi.</Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ fontFamily: 'Orbitron, sans-serif', color: '#006d77', mb: 1, mt: 3 }}> ALEGE UN PLAN </Typography>
            {plans.map(plan => (
              <Paper
                key={plan.id}
                elevation={selectedPlan?.id === plan.id ? 2 : 0}
                variant="outlined"
                sx={{ p: 2, mb: 1.5, cursor: 'pointer', borderColor: selectedPlan?.id === plan.id ? '#dd4124' : '#eee', transition: '0.2s', '&:hover': { borderColor: '#dd4124' } }}
                onClick={() => setSelectedPlan(plan)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#006d77' }}>{plan.name}</Typography>
                  <Typography variant="h6" sx={{ color: '#dd4124', fontWeight: 'bold' }}>{plan.totalPrice} RON</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                   {plan.monthsPaid} luni {plan.bonusMonths > 0 && `+ ${plan.bonusMonths} BONUS`} • {plan.monthlyPrice.toFixed(1)} RON / lună
                </Typography>
              </Paper>
            ))}

            {selectedPlan && (
              <Box sx={{ mt: 4 }}>
                 <Divider sx={{ mb: 3 }} />
                 <Typography variant="subtitle2" sx={{ fontFamily: 'Orbitron, sans-serif', color: '#006d77', mb: 1 }}> FINALIZARE PLATĂ </Typography>
                 <Elements stripe={stripeObject}>
                   <HostSubscriptionCheckout
                     token={token}
                     plan={selectedPlan}
                     onSuccess={() => {
                       setSubscription({ ...subscription, subscriptionPaid: true });
                       setSelectedPlan(null);
                     }}
                   />
                 </Elements>
              </Box>
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Container>
    </Box>
  );
}